import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { Trash2, Edit2, Plus, Save, X, Server } from 'lucide-react';

const MainTechManage = () => {
    const [techs, setTechs] = useState([]);
    const [name, setName] = useState('');
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', msg: '' });

    // Styles
    const inputStyles = "bg-gray-800 border border-gray-700 text-gray-200 p-3 rounded-lg w-full focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-300";
    const btnStyles = "flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300";

    // 1. Fetch Data
    const fetchTechs = async () => {
        try {
            const res = await axiosInstance.get('/main-tech');
            setTechs(res.data);
        } catch (err) {
            console.error("Failed to fetch main techs", err);
        }
    };

    useEffect(() => {
        fetchTechs();
    }, []);

    // 2. Handle Submit (Create or Update)
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;

        setLoading(true);
        setStatus({ type: '', msg: '' });

        try {
            if (editId) {
                // Update
                await axiosInstance.put(`/main-tech/${editId}`, { name });
                setStatus({ type: 'success', msg: 'Updated successfully!' });
            } else {
                // Create
                await axiosInstance.post('/main-tech', { name });
                setStatus({ type: 'success', msg: 'Created successfully!' });
            }
            
            // Reset Form and Refresh List
            setName('');
            setEditId(null);
            fetchTechs();

        } catch (err) {
            setStatus({ type: 'error', msg: 'Operation failed. Please try again.' });
            console.error(err);
        } finally {
            setLoading(false);
            // Clear status after 3 seconds
            setTimeout(() => setStatus({ type: '', msg: '' }), 3000);
        }
    };

    // 3. Handle Delete
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this Tech? Products using this tech might be affected.")) return;

        try {
            await axiosInstance.delete(`/main-tech/${id}`);
            fetchTechs();
            setStatus({ type: 'success', msg: 'Deleted successfully!' });
        } catch (err) {
            setStatus({ type: 'error', msg: 'Failed to delete.' });
            console.error(err);
        }
    };

    // 4. Populate Edit Form
    const handleEdit = (tech) => {
        setName(tech.name);
        setEditId(tech.id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // 5. Cancel Edit
    const handleCancel = () => {
        setName('');
        setEditId(null);
        setStatus({ type: '', msg: '' });
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            
            {/* --- HEADER --- */}
            <div className="flex items-center gap-4 mb-8 text-gray-100">
                <div className="p-3 bg-blue-600/20 rounded-lg text-blue-400">
                    <Server size={32} />
                </div>
                <div>
                    <h1 className="text-3xl font-bold">Main Tech Management</h1>
                    <p className="text-gray-400 text-sm">Manage technologies used in product filters (e.g., React, PHP, Node.js)</p>
                </div>
            </div>

            {/* --- FORM SECTION --- */}
            <div className="bg-gray-800/50 border border-gray-700 p-6 rounded-2xl shadow-xl mb-10">
                <h2 className="text-xl font-bold text-gray-200 mb-4 flex items-center gap-2">
                    {editId ? <Edit2 size={20} className="text-yellow-400"/> : <Plus size={20} className="text-green-400"/>}
                    {editId ? 'Edit Technology' : 'Add New Technology'}
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 items-start">
                    <div className="flex-grow w-full">
                        <input 
                            type="text" 
                            value={name} 
                            onChange={(e) => setName(e.target.value)} 
                            placeholder="e.g. Laravel, React Native" 
                            className={inputStyles}
                            required
                        />
                    </div>
                    
                    <div className="flex gap-2 w-full md:w-auto">
                        <button 
                            type="submit" 
                            disabled={loading}
                            className={`${btnStyles} ${editId ? 'bg-yellow-600 hover:bg-yellow-500 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'} w-full md:w-auto`}
                        >
                            {loading ? 'Processing...' : (editId ? <><Save size={18}/> Update</> : <><Plus size={18}/> Add</>)}
                        </button>

                        {editId && (
                            <button 
                                type="button" 
                                onClick={handleCancel}
                                className={`${btnStyles} bg-gray-600 hover:bg-gray-500 text-white w-full md:w-auto`}
                            >
                                <X size={18}/> Cancel
                            </button>
                        )}
                    </div>
                </form>

                {/* Status Message */}
                {status.msg && (
                    <div className={`mt-4 p-3 rounded-lg text-sm font-semibold text-center ${status.type === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                        {status.msg}
                    </div>
                )}
            </div>

            {/* --- LIST SECTION --- */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6 border-b border-gray-800">
                    <h2 className="text-lg font-bold text-gray-300">Existing Technologies ({techs.length})</h2>
                </div>

                {techs.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        No technologies found. Add one above.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-800 text-gray-400 text-xs uppercase tracking-wider">
                                    <th className="p-4 border-b border-gray-700">ID</th>
                                    <th className="p-4 border-b border-gray-700">Name</th>
                                    <th className="p-4 border-b border-gray-700 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {techs.map((tech) => (
                                    <tr key={tech.id} className="hover:bg-gray-800/50 transition-colors group">
                                        <td className="p-4 text-gray-500 font-mono text-sm">#{tech.id}</td>
                                        <td className="p-4 text-gray-200 font-semibold">{tech.name}</td>
                                        <td className="p-4 flex justify-end gap-3">
                                            <button 
                                                onClick={() => handleEdit(tech)}
                                                className="p-2 bg-gray-700 hover:bg-yellow-600 text-yellow-400 hover:text-white rounded-lg transition-colors"
                                                title="Edit"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(tech.id)}
                                                className="p-2 bg-gray-700 hover:bg-red-600 text-red-400 hover:text-white rounded-lg transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MainTechManage;